import React, { useRef, useState, useEffect } from 'react';
import * as faceapi from '@vladmandic/face-api';
import { Camera, X, RefreshCw, CheckCircle2, AlertTriangle, SwitchCamera, Sparkles, Check, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { loadFaceApiModels, areFaceApiModelsLoaded } from '@/utils/faceApiLoader';

export default function FaceRegistrationModal({ isOpen, onClose, onRegistered }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [isModelLoaded, setIsModelLoaded] = useState(() => areFaceApiModelsLoaded());
  const [status, setStatus] = useState(() => areFaceApiModelsLoaded() ? 'Model AI siap. Posisikan wajah di dalam panduan.' : 'Menyiapkan AI Wajah...');
  const [isDetecting, setIsDetecting] = useState(false);
  const [facingMode, setFacingMode] = useState('user'); // 'user' (depan) atau 'environment' (belakang)

  // Freeze Snapshot & Shutter Flash States
  const [frozenPhoto, setFrozenPhoto] = useState(null);
  const [isFlashing, setIsFlashing] = useState(false);
  const [scanSuccess, setScanSuccess] = useState(false);
  const [tempDescriptor, setTempDescriptor] = useState(null);

  // Efek suara shutter kamera sintetis (Web Audio API)
  const playShutterSound = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const audioCtx = new AudioCtx();
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(800, audioCtx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(100, audioCtx.currentTime + 0.08);

      gain.gain.setValueAtTime(0.3, audioCtx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.08);

      osc.connect(gain);
      gain.connect(audioCtx.destination);

      osc.start();
      osc.stop(audioCtx.currentTime + 0.09);
    } catch {
      // Abaikan jika browser membatasi autoplay audio
    }
  };

  useEffect(() => {
    if (!isOpen) {
      stopCamera();
      setFrozenPhoto(null);
      setIsFlashing(false);
      setIsDetecting(false);
      setScanSuccess(false);
      setTempDescriptor(null);
      return;
    }

    startCamera(facingMode);

    loadFaceApiModels().then((success) => {
      if (success) {
        setIsModelLoaded(true);
        setStatus('Model AI siap. Arahkan wajah ke kamera lalu klik tombol ambil foto.');
      } else {
        setStatus('Gagal memuat model AI. Periksa koneksi internet.');
      }
    });

    return () => {
      stopCamera();
    };
  }, [isOpen, facingMode]);

  const startCamera = async (mode) => {
    stopCamera();
    try {
      const constraints = {
        video: { 
          facingMode: mode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
          aspectRatio: { ideal: 1.333333 }
        }
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      streamRef.current = stream;
      setStatus('Posisikan wajah di dalam bingkai, lalu klik Ambil Foto.');
    } catch (err) {
      console.error("Camera error:", err);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        streamRef.current = stream;
      } catch (err2) {
        setStatus('Gagal membuka kamera. Pastikan izin kamera telah diberikan.');
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const toggleFlipCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  const createSnapshotCanvas = (video) => {
    const canvas = document.createElement('canvas');
    const maxDim = 640;
    let w = video.videoWidth || 640;
    let h = video.videoHeight || 480;
    if (w > maxDim || h > maxDim) {
      if (w > h) { h = Math.round(h * (maxDim / w)); w = maxDim; }
      else { w = Math.round(w * (maxDim / h)); h = maxDim; }
    }
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    
    // Jika kamera depan, balikkan secara horizontal agar foto acuan terlihat natural seperti cermin
    if (facingMode === 'user') {
      ctx.translate(w, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, w, h);
    return canvas;
  };

  const captureFace = async () => {
    if (!videoRef.current) return;
    
    // 1. INSTANT SNAPSHOT & FREEZE FRAME
    // Foto langsung dibekukan saat ini juga tanpa delay, sehingga tidak berubah saat HP bergerak/turun
    let base64Image = null;
    let snapshotCanvas = null;
    try {
      snapshotCanvas = createSnapshotCanvas(videoRef.current);
      base64Image = snapshotCanvas.toDataURL('image/jpeg', 0.85);
      setFrozenPhoto(base64Image);
      setIsFlashing(true);
      playShutterSound();
      setTimeout(() => setIsFlashing(false), 200);
    } catch (snapErr) {
      console.warn("Snapshot capture error", snapErr);
    }

    setIsDetecting(true);
    setStatus('📸 Foto terkunci! AI sedang memindai biometrik wajah...');

    try {
      // Helper timeout agar AI tidak pernah membuat aplikasi macet
      const withTimeout = (promise, ms = 4000) => {
        return Promise.race([
          promise,
          new Promise((_, reject) => setTimeout(() => reject(new Error('AI detection timeout')), ms))
        ]);
      };

      try {
        if (!areFaceApiModelsLoaded()) {
          await withTimeout(loadFaceApiModels(), 3000);
        }
      } catch (loadErr) {
        console.warn("Face model load timeout:", loadErr);
      }

      let detection = null;

      // Prioritas 1: Deteksi langsung dari frame kanvas yang baru saja dibekukan
      if (areFaceApiModelsLoaded() && snapshotCanvas) {
        try {
          const detectPromise = faceapi.detectSingleFace(
            snapshotCanvas,
            new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.12 })
          )
          .withFaceLandmarks()
          .withFaceDescriptor();

          detection = await withTimeout(detectPromise, 3500);
        } catch (err) {
          console.warn("Canvas detection error/timeout:", err);
        }
      }

      // Prioritas 2: Jika kanvas belum terdeteksi, coba dari video element
      if (!detection && areFaceApiModelsLoaded() && videoRef.current) {
        try {
          const detectPromise = faceapi.detectSingleFace(
            videoRef.current,
            new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.12 })
          )
          .withFaceLandmarks()
          .withFaceDescriptor();

          detection = await withTimeout(detectPromise, 2500);
        } catch (err) {
          console.warn("Video detection error/timeout:", err);
        }
      }

      if (!detection) {
        setStatus('Wajah tidak terdeteksi jelas. Pastikan wajah tegak, terang, dan menghadap kamera.');
        setIsDetecting(false);
        setScanSuccess(false);
        return;
      }

      const descriptorArray = Array.from(detection.descriptor);
      const descriptorString = JSON.stringify(descriptorArray);

      setTempDescriptor(descriptorString);
      setScanSuccess(true);
      setIsDetecting(false);
      setStatus('✅ Wajah berhasil dipindai dan terkunci!');

    } catch (err) {
      console.error(err);
      setStatus('Terjadi kesalahan saat memproses AI.');
      setIsDetecting(false);
      setScanSuccess(false);
    }
  };

  const handleRetake = () => {
    setFrozenPhoto(null);
    setScanSuccess(false);
    setTempDescriptor(null);
    setIsDetecting(false);
    setStatus('Posisikan wajah di dalam bingkai, lalu klik Ambil Foto.');
    if (!streamRef.current) {
      startCamera(facingMode);
    }
  };

  const handleConfirmSave = () => {
    if (tempDescriptor && frozenPhoto) {
      onRegistered(tempDescriptor, frozenPhoto);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent 
        className="sm:max-w-md p-0 border-none bg-transparent shadow-none overflow-visible"
        hideCloseButton={true}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Pindai Wajah Acuan</DialogTitle>
        <DialogDescription className="sr-only">Daftarkan foto dan biometrik wajah acuan untuk absensi</DialogDescription>
        
        <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden relative shadow-2xl pointer-events-auto border border-slate-100">
          
          {/* Header */}
          <div className="p-4 px-5 pb-3.5 border-b border-gray-100 flex items-center justify-between bg-white">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shadow-sm">
                <Camera className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-bold text-sm text-slate-800 flex items-center gap-1.5">
                  Foto & Biometrik Acuan
                  <span className="text-[10px] bg-indigo-50 text-indigo-600 font-semibold px-2 py-0.5 rounded-full border border-indigo-100">
                    Master Wajah
                  </span>
                </h3>
                <p className="text-[11px] text-slate-400">Digunakan sebagai acuan pencocokan saat absen</p>
              </div>
            </div>

            <button 
              type="button" 
              onClick={onClose} 
              className="p-1.5 bg-gray-100/80 text-gray-500 hover:text-gray-700 hover:bg-gray-200/80 rounded-full transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="p-5 flex flex-col items-center">
            
            {/* Viewfinder Kamera */}
            <div className="relative w-full aspect-[4/3] max-h-[45vh] bg-slate-950 rounded-2xl overflow-hidden shadow-inner mb-3.5 group">
              
              {/* Live Video */}
              <video 
                ref={videoRef}
                autoPlay 
                playsInline 
                muted 
                className={`w-full h-full object-cover ${facingMode === 'user' ? 'scale-x-[-1]' : ''}`}
              />

              {/* Frozen Snapshot Image (Diam seketika saat tombol ditekan) */}
              {frozenPhoto && (
                <img 
                  src={frozenPhoto} 
                  alt="Wajah Acuan Terkunci" 
                  className="absolute inset-0 w-full h-full object-cover z-10 animate-in fade-in duration-150"
                />
              )}

              {/* Shutter White Flash Animation */}
              <div 
                className={`absolute inset-0 bg-white z-40 transition-opacity duration-200 pointer-events-none ${
                  isFlashing ? 'opacity-90' : 'opacity-0'
                }`} 
              />

              {/* Status Overlay Spinner */}
              {isDetecting && (
                <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/60 backdrop-blur-xs text-white p-4">
                  <RefreshCw className="w-9 h-9 text-indigo-400 animate-spin mb-2" />
                  <span className="text-xs font-bold tracking-wide">Menganalisis Wajah...</span>
                </div>
              )}

              {/* Sukses Badge Overlay */}
              {scanSuccess && !isDetecting && (
                <div className="absolute top-3 left-3 z-30 flex items-center gap-1.5 bg-emerald-600/90 backdrop-blur-md text-white px-2.5 py-1 rounded-full text-xs font-semibold shadow-md animate-in fade-in slide-in-from-top-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-200" />
                  Wajah Terkunci & Valid
                </div>
              )}

              {/* Tombol Flip Kamera (Hanya saat belum freeze) */}
              {!frozenPhoto && (
                <button
                  type="button"
                  onClick={toggleFlipCamera}
                  title="Ganti Kamera Depan / Belakang"
                  className="absolute top-3 right-3 z-20 p-2 rounded-full bg-black/50 hover:bg-black/75 text-white backdrop-blur-md border border-white/20 transition-all shadow-md active:scale-95"
                >
                  <SwitchCamera className="w-4 h-4" />
                </button>
              )}

              {/* Oval Face Guide Frame (hanya saat belum ada frozen foto) */}
              {!frozenPhoto && (
                <>
                  <div className="absolute inset-0 border-2 border-indigo-500/20 rounded-2xl pointer-events-none"></div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-52 border-2 border-dashed border-white/60 rounded-[45%] pointer-events-none shadow-sm flex items-center justify-center">
                    <span className="text-[10px] text-white/75 bg-black/40 px-2 py-0.5 rounded-full font-medium tracking-wide">
                      Arahkan Wajah Sini
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Status Text Bar */}
            <div className={`w-full p-2.5 px-3.5 rounded-xl text-xs font-medium text-center mb-4 flex items-center justify-center gap-1.5 transition-all ${
              scanSuccess 
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : frozenPhoto && !scanSuccess && !isDetecting
                ? 'bg-rose-50 text-rose-700 border border-rose-200'
                : 'bg-slate-50 text-slate-600 border border-slate-100'
            }`}>
              {scanSuccess ? (
                <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              ) : frozenPhoto && !scanSuccess && !isDetecting ? (
                <AlertTriangle className="w-3.5 h-3.5 text-rose-600 shrink-0" />
              ) : (
                <Sparkles className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
              )}
              <span className="leading-snug">{status}</span>
            </div>

            {/* Action Buttons */}
            {!frozenPhoto ? (
              <Button 
                onClick={captureFace} 
                disabled={!isModelLoaded || isDetecting}
                className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-md shadow-indigo-200 transition-all flex items-center justify-center gap-2"
              >
                <Camera className="w-4 h-4" /> Ambil & Kunci Foto Wajah
              </Button>
            ) : (
              <div className="grid grid-cols-2 gap-2.5 w-full">
                <Button 
                  variant="outline"
                  onClick={handleRetake} 
                  disabled={isDetecting}
                  className="h-11 border-slate-200 text-slate-700 hover:bg-slate-100 font-semibold rounded-xl flex items-center justify-center gap-1.5"
                >
                  <RotateCcw className="w-4 h-4" /> Foto Ulang
                </Button>

                <Button 
                  onClick={handleConfirmSave} 
                  disabled={!scanSuccess || isDetecting}
                  className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-md shadow-emerald-200 flex items-center justify-center gap-1.5"
                >
                  <Check className="w-4 h-4" /> Gunakan Foto Ini
                </Button>
              </div>
            )}

          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

